import { css } from '@/styled-system/css';
import { MIN_LG } from '@/src/lib/breakpoints';
import { buildStaticMapProxyUrl, buildStaticMapUrlForBounds, type StaticMapBounds } from '@/src/lib/utils/staticMap';

// /api/static-map's dimension cap; scale 2 keeps the panel sharp, jpg is 4-6x smaller than png.
const PREVIEW_SIZE = 640;

const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

const previewPicture = css({
  width: '100%',
  height: '100%',
  display: 'block',
});

const previewImage = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  // The Maps Static API has no dark rendering, so dark mode blends the light image in CSS.
  _dark: { filter: 'invert(1) hue-rotate(180deg) opacity(0.5)' },
});

export function buildStaticMapPreviewUrl(bounds: StaticMapBounds): string {
  return buildStaticMapProxyUrl(
    buildStaticMapUrlForBounds(bounds, { width: PREVIEW_SIZE, height: PREVIEW_SIZE, scale: 2, format: 'jpg' }),
  );
}

/**
 * Static snapshot painted before the interactive map mounts so the desktop LCP
 * candidate is a preload-scannable same-origin image; the desktop-only <source>
 * keeps mobile from downloading it.
 */
export function StaticMapPreview({ bounds, onError }: { bounds: StaticMapBounds; onError?: () => void }) {
  return (
    <picture className={previewPicture}>
      <source media={MIN_LG} srcSet={buildStaticMapPreviewUrl(bounds)} />
      {/* fetchPriority rides whichever source the browser selects: the lg+ map
          on desktop, the no-fetch data pixel on mobile — so the hint cannot
          regress mobile. Without it Chrome queues the map at low priority
          behind the card images and the map-LCP runs blow past 3.5s (HP-8668). */}
      <img src={TRANSPARENT_PIXEL} alt="" fetchPriority="high" className={previewImage} onError={onError} />
    </picture>
  );
}
